import React, {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {Icon} from '@iconify/react';
import {loggedInUserSelector} from '../../../store/selectors/auth.selectors';
import MessagesContainer from './components/MessagesContainer';
import Button from '../../../incase-domain/components/button/Button';
import PageHeader from '../../components/common/header/PageHeader';
import {useConnections, useConversations, useDeleteConversation, useStartConversation} from './queries';
import ConversationsSearch from './components/ConversationsSearch';
import ConversationsList from './components/ConversationsList';

function MessagesPage(): JSX.Element {
    const {t} = useTranslation();
    const {conversationId} = useParams();

    const loggedInUser = useSelector(loggedInUserSelector);
    const [searchString, setSearchString] = useState<string>('');

    const {
        data: conversations,
        isLoading,
        fetchNextPage,
        hasNextPage,
    } = useConversations({
        searchString,
        searchBy: 'email',
        pageSize: 20,
        refetchInterval: 5000,
    });

    const {data: connections} = useConnections({
        searchString,
        pageSize: 20,
    });

    const allConnections = useMemo(() => {
        return connections?.pages.flatMap((page) => page.content) || [];
    }, [connections?.pages]);

    const allConversations = useMemo(() => {
        return conversations?.pages.flatMap((page) => page.content) || [];
    }, [conversations?.pages]);

    const selectedConversation = allConversations.find((c) => c._id === conversationId);

    const filteredConnections = allConnections.filter((c) => {
        return !allConversations.find((conv) => conv.participantsDetailed.find((p) => p.userId === c.userId));
    });

    const startConversation = useStartConversation({
        loggedInUserId: loggedInUser?._id || '',
        loggedInUserEmail: loggedInUser?.email || '',
        onSuccess: () => {
            setSearchString('');
        },
    });

    const {handleDeleteConversationAction} = useDeleteConversation({
        allConversations,
    });

    return (
        <div className="l-container">
            <PageHeader title={t('navigation.messages')} />
            <div className="p-messaging__wrapper">
                <div className="p-messaging__sidebar">
                    <ConversationsSearch searchString={searchString} setSearchString={setSearchString} />

                    <div className="c-conversations">
                        {searchString && allConversations.length > 0 && (
                            <ConversationsList setSearchString={setSearchString} allConversations={allConversations} />
                        )}

                        {searchString && filteredConnections.length > 0 && (
                            <>
                                <div className="c-conversations__groupHeader">{t('messaging.connections')}</div>
                                <div className="c-conversations__search_list">
                                    {filteredConnections.map((connection) => (
                                        <Button
                                            data-cy="conversation"
                                            variant="ghost"
                                            align="left"
                                            size="lg"
                                            leading="solar:user-rounded-linear"
                                            key={connection._id}
                                            onClick={() => startConversation.mutate(connection)}
                                        >
                                            {connection.email}
                                        </Button>
                                    ))}
                                </div>
                            </>
                        )}

                        {!searchString && (
                            <div id="scrollableConversations" className="c-conversations__list">
                                <ConversationsList
                                    setSearchString={setSearchString}
                                    allConversations={allConversations}
                                    showHeader={false}
                                    infiniteScroll={{
                                        fetchNextPage,
                                        hasNextPage: hasNextPage || false,
                                        scrollableTargetId: 'scrollableConversations',
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
                {isLoading ? (
                    <div className="p-messaging__loading">
                        <Icon icon="svg-spinners:90-ring" />
                    </div>
                ) : (
                    <>
                        {conversationId && allConversations.length !== 0 && (
                            <div className="p-messaging__content">
                                <MessagesContainer
                                    key={conversationId}
                                    conversation={selectedConversation}
                                    handleDeleteConversation={handleDeleteConversationAction}
                                />
                            </div>
                        )}
                        {allConversations.length === 0 && (
                            <div className="c-errorMessage" style={{width: '100%'}}>
                                {t('messaging.noConversations')}
                            </div>
                        )}
                        {!conversationId && allConversations.length > 0 && (
                            <div className="c-errorMessage" style={{width: '100%'}}>
                                {t('messaging.noSelectedConversation')}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default MessagesPage;
